import {MenuAccess} from '@/app/modules/user-management/user-management-edit/core/_model'

export type PageItem = {
  title: string
  path: string
  icon?: string
  parent?: string
}

const STOPWORDS = new Set([
  'yang',
  'dan',
  'untuk',
  'dengan',
  'dari',
  'ke',
  'di',
  'pada',
  'adalah',
  'saya',
  'kamu',
  'cari',
  'tolong',
  'bisa',
  'mau',
  'ingin',
  'mana',
  'dimana',
  'apa',
  'bagaimana',
  'buat',
  'lihat',
  'tampilkan',
  'buka',
  'mohon',
  'halaman',
  'menu',
])

// Ekspansi sinonim agar pencarian menangkap variasi istilah (Indonesia/Inggris).
// Key multi-kata dicocokkan sebagai frasa utuh, key satu kata sebagai token.
const ALIASES: Record<string, string[]> = {
  // Laporan
  laporan: ['laporan', 'report', 'rekap', 'grafik', 'chart', 'analisis', 'analysis'],
  report: ['laporan', 'report', 'rekap', 'grafik', 'chart', 'analisis'],
  rekap: ['laporan', 'rekap', 'report', 'grafik'],
  // Penjualan
  penjualan: ['penjualan', 'sales', 'jual', 'omzet', 'revenue', 'pendapatan'],
  sales: ['sales', 'penjualan', 'jual', 'omzet', 'revenue'],
  omzet: ['omzet', 'revenue', 'pendapatan', 'penjualan', 'sales'],
  // Produk
  produk: ['produk', 'product', 'barang', 'item'],
  product: ['produk', 'product', 'barang', 'item'],
  barang: ['produk', 'product', 'barang', 'item'],
  // Bahan baku
  'bahan baku': ['bahan baku', 'raw material', 'bahan', 'material'],
  'raw material': ['raw material', 'bahan baku', 'bahan', 'material'],
  bahan: ['bahan baku', 'raw material', 'bahan', 'material'],
  // Stok
  stok: ['stok', 'stock', 'persediaan', 'inventory', 'sisa'],
  stock: ['stok', 'stock', 'persediaan', 'inventory'],
  persediaan: ['stok', 'stock', 'persediaan', 'inventory'],
  opname: ['opname', 'stock opname', 'inventaris', 'stok'],
  // Purchase order / pembelian
  'purchase order': ['purchase order', 'po', 'pembelian', 'beli', 'supplier order'],
  pembelian: ['pembelian', 'purchase order', 'po', 'beli'],
  supplier: ['supplier', 'pemasok', 'vendor'],
  pemasok: ['supplier', 'pemasok', 'vendor'],
  // Transfer
  transfer: ['transfer', 'mutasi', 'pindah'],
  // SDM
  karyawan: ['karyawan', 'employee', 'pegawai', 'staff'],
  employee: ['karyawan', 'employee', 'pegawai', 'staff'],
  pegawai: ['karyawan', 'employee', 'pegawai', 'staff'],
  kontrak: ['kontrak', 'contract'],
  absensi: ['absensi', 'attendance', 'kehadiran'],
  attendance: ['absensi', 'attendance', 'kehadiran'],
  gaji: ['gaji', 'payroll', 'salary', 'upah'],
  payroll: ['gaji', 'payroll', 'salary', 'upah'],
  // Customer
  customer: ['customer', 'pelanggan', 'klien'],
  pelanggan: ['customer', 'pelanggan', 'klien'],
  // Finance & accounting
  invoice: ['invoice', 'faktur', 'tagihan'],
  faktur: ['invoice', 'faktur', 'tagihan'],
  jurnal: ['journal', 'jurnal'],
  journal: ['journal', 'jurnal'],
  'general ledger': ['general ledger', 'buku besar', 'ledger'],
  'laba rugi': ['laba rugi', 'profit loss', 'profit and loss', 'pnl'],
  'profit loss': ['laba rugi', 'profit loss', 'profit and loss', 'pnl'],
  neraca: ['neraca', 'balance sheet'],
  'balance sheet': ['neraca', 'balance sheet'],
  akuntansi: ['akuntansi', 'accounting', 'finance of accounting', 'keuangan'],
  accounting: ['akuntansi', 'accounting', 'finance of accounting', 'keuangan'],
  // Kasir / POS
  kasir: ['kasir', 'pos', 'point of sale'],
  pos: ['kasir', 'pos', 'point of sale'],
  // Transaksi
  transaksi: ['transaksi', 'transaction', 'history', 'riwayat'],
  transaction: ['transaksi', 'transaction', 'history', 'riwayat'],
  riwayat: ['riwayat', 'history', 'transaksi', 'transaction'],
  // Order / booking
  order: ['order', 'pesanan', 'booking', 'reservasi'],
  pesanan: ['order', 'pesanan', 'booking'],
  booking: ['booking', 'order', 'pesanan', 'reservasi'],
  // Rating
  rating: ['rating', 'penilaian', 'ulasan', 'review'],
  ulasan: ['rating', 'penilaian', 'ulasan', 'review'],
  // KPI
  kpi: ['kpi', 'kinerja', 'performance'],
  kinerja: ['kpi', 'kinerja', 'performance'],
  // Outlet
  outlet: ['outlet', 'cabang', 'toko', 'store'],
  cabang: ['outlet', 'cabang', 'toko', 'store'],
  toko: ['outlet', 'toko', 'store'],
  hotel: ['hotel', 'partner', 'partner organization'],
  // CMS / konten
  cms: ['cms', 'konten', 'content'],
  konten: ['cms', 'konten', 'content'],
  // Approval
  approval: ['approval', 'persetujuan', 'setujui', 'approve'],
  persetujuan: ['approval', 'persetujuan', 'setujui', 'approve'],
  // Receive
  receive: ['receive', 'terima', 'penerimaan'],
  terima: ['receive', 'terima', 'penerimaan'],
  // Forecast / budget
  forecast: ['forecast', 'prediksi', 'peramalan', 'budget', 'anggaran', 'target'],
  budget: ['budget', 'anggaran', 'forecast', 'target'],
  anggaran: ['budget', 'anggaran', 'forecast', 'target'],
  target: ['target', 'budget', 'forecast', 'anggaran'],
  // Dashboard
  dashboard: ['dashboard', 'beranda', 'home', 'ringkasan'],
  beranda: ['dashboard', 'beranda', 'home', 'ringkasan'],
  // Chart of account
  'chart of account': ['chart of account', 'coa', 'akun'],
  // CMS / marketing
  marketing: ['marketing', 'pemasaran', 'sales marketing'],
  // Booking / survey
  survey: ['survey', 'survei', 'kuesioner'],
  survei: ['survey', 'survei', 'kuesioner'],
  // Daily checkin / meeting
  'daily checkin': ['daily checkin', 'checkin', 'check-in', 'absensi harian'],
  'minute of meeting': ['minute of meeting', 'mom', 'notulen', 'rapat'],
  'briefing report': ['briefing report', 'briefing', 'laporan briefing'],
  // Maps
  maps: ['maps', 'peta', 'lokasi', 'map'],
  peta: ['maps', 'peta', 'lokasi'],
}

const stripHtml = (t?: string): string =>
  (t || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * Flatten tree menu access user menjadi daftar halaman yang bisa dinavigasi.
 * Item tanpa url_path (section header) dipakai sebagai breadcrumb parent.
 */
export function flattenPages(menus: MenuAccess[] | undefined, parent?: string): PageItem[] {
  const out: PageItem[] = []
  const seen = new Set<string>()

  const walk = (items: MenuAccess[] | undefined, par: string | undefined) => {
    for (const m of items || []) {
      const title = stripHtml(m.text)
      const urlPath = m.url_path
      if (urlPath && urlPath !== '#') {
        if (!seen.has(urlPath)) {
          seen.add(urlPath)
          out.push({title, path: urlPath, icon: m.icon, parent: par})
        }
        walk(m.sub_menu, title)
      } else {
        // Section header tanpa halaman — child mewarisi parent baru
        walk(m.sub_menu, title ? title : par)
      }
    }
  }

  walk(menus, parent)
  return out
}

function expandTerms(query: string): Set<string> {
  const q = query.toLowerCase()
  const terms = new Set<string>()

  // Frasa alias multi-kata (cocok sebagai substring utuh)
  for (const [key, vals] of Object.entries(ALIASES)) {
    if (key.includes(' ') && q.includes(key)) {
      terms.add(key)
      vals.forEach((v) => terms.add(v))
    }
  }

  const tokens = q
    .split(/[^a-z0-9]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 1 && !STOPWORDS.has(t))
  for (const t of tokens) {
    terms.add(t)
    const extra = ALIASES[t]
    if (extra) extra.forEach((v) => terms.add(v))
  }

  return terms
}

/**
 * Cari halaman yang relevan dengan query. Skor: judul cocok penuh > judul
 * mengandung query > judul/path/parent mengandung term (dengan alias).
 */
export function searchPages(query: string, pages: PageItem[], topN = 7): PageItem[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  const terms = expandTerms(q)

  const scored = pages.map((p) => {
    const title = p.title.toLowerCase()
    const path = p.path.toLowerCase()
    const parent = (p.parent || '').toLowerCase()
    let score = 0

    if (title === q) score += 20
    else if (title.includes(q)) score += 10
    else if (title.startsWith(q)) score += 5

    terms.forEach((term) => {
      if (term.length < 2) return
      if (title.includes(term)) score += 3
      if (path.includes(term)) score += 2
      if (parent.includes(term)) score += 1
    })

    return {p, score}
  })

  const hits = scored.filter((s) => s.score > 0)
  hits.sort(
    (a, b) =>
      b.score - a.score || a.p.title.length - b.p.title.length || a.p.title.localeCompare(b.p.title)
  )
  return hits.slice(0, topN).map((s) => s.p)
}
